import React, { useCallback } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';

import { useLuminaViewEnded } from '../hooks/use-lumina-view-ended';
import type { LuminaViewEnded } from './view-ended';
import { useLuminaViewEndedContext } from './view-ended-context';

const styles = cssMap({
	container: {
		width: '100%',
	},
	carouselContainer: {
		height: '100%',
		width: '100%',
	},
});

type LuminaViewTrackerProps = Omit<LuminaViewEnded, 'dwellTimeSeconds' | 'layoutIndex'> & {
	children: React.ReactNode;
	/** Layout-only context for sizing carousel smart links; not included in analytics. */
	containerType?: 'carousel';
};

export const LuminaViewTracker = ({
	children,
	componentType,
	containerType,
}: LuminaViewTrackerProps): React.JSX.Element => {
	const { layoutIndex, onViewEnded, sessionId } = useLuminaViewEndedContext();
	const handleViewEnded = useCallback(
		({ dwellTimeSeconds }: { dwellTimeSeconds: number }) => {
			onViewEnded?.({
				componentType,
				dwellTimeSeconds,
				layoutIndex,
			});
		},
		[componentType, layoutIndex, onViewEnded],
	);
	const containerRef = useLuminaViewEnded({
		onViewEnded: onViewEnded ? handleViewEnded : undefined,
		sessionId,
	});

	if (!onViewEnded) {
		return <React.Fragment>{children}</React.Fragment>;
	}

	return (
		<Box
			ref={containerRef}
			xcss={
				containerType === 'carousel' && componentType === 'smartLink'
					? styles.carouselContainer
					: styles.container
			}
		>
			{children}
		</Box>
	);
};
